import { computed, ref, type Ref } from 'vue'
import { defineStore } from 'pinia'
import { useApi } from '../../composables/api'
import { useAuth0 } from '@auth0/auth0-vue'

export type UserRole = 'User' | 'Staff' | 'Anonymous'

// Clock skew buffer: treat token as expired 30s before its stated expiry
const CLOCK_SKEW_MS = 30_000

export const useSessionStore = defineStore(
  'session',
  () => {
    const { heartbeatApi, sessionApi } = useApi()

    const {
      loginWithRedirect,
      logout,
      user,
      isAuthenticated: _isAuthenticated,
      getAccessTokenSilently,
      isLoading: isLoggingIn,
      error: _authError
    } = useAuth0()

    // Heartbeat
    const isCheckingHeartbeat = ref(false)
    const serviceHealthy = ref(false)
    const isOnline = computed(() => serviceHealthy.value)

    // Only show loading overlay while Auth0 is doing its own init/callback.
    // Do NOT include !isAuthenticated here — that would cover the Turnstile challenge.
    const isLoading = computed(() => (isLoggingIn as unknown as Ref<boolean>).value)

    async function heartbeat() {
      if (isCheckingHeartbeat.value) return
      isCheckingHeartbeat.value = true
      try {
        await heartbeatApi.heartbeatCheckHeartbeat()
        serviceHealthy.value = true
      } catch {
        serviceHealthy.value = false
      } finally {
        isCheckingHeartbeat.value = false
      }
    }

    // Anonymous sessions
    // expiresAt is stored in milliseconds internally (backend returns Unix seconds).
    const anonymousAccessToken = ref<string | null>(null)
    const anonymousTokenExpiresAt = ref<number | null>(null)

    function clearAnonymousSession() {
      anonymousAccessToken.value = null
      anonymousTokenExpiresAt.value = null
    }

    // Schedules a timeout to clear the anonymous session when it lapses.
    // Called both after a fresh challenge and after store hydration from storage.
    function scheduleAnonymousSessionExpiry() {
      if (!anonymousAccessToken.value || !anonymousTokenExpiresAt.value) return
      const remainingMs = anonymousTokenExpiresAt.value - Date.now() - CLOCK_SKEW_MS
      if (remainingMs <= 0) {
        clearAnonymousSession()
        return
      }
      setTimeout(clearAnonymousSession, remainingMs)
    }

    // Clears the Auth0 local cache without redirecting to the Auth0 logout endpoint.
    // Used when a refresh token is irrecoverably invalid so the app can degrade gracefully
    // to anonymous mode rather than looping on broken silent-refresh attempts.
    async function clearAuthSession() {
      await logout({ openUrl: false })
    }

    async function initialiseAnonymousSession(turnstileToken: string) {
      try {
        const response = await sessionApi.sessionStartAnonymousSession({ turnstileToken })
        anonymousAccessToken.value = response.accessToken ?? null
        if (response.expiresAt) {
          anonymousTokenExpiresAt.value = response.expiresAt * 1000 // seconds → ms
          scheduleAnonymousSessionExpiry()
        }
      } catch (e) {
        console.error('Failed to initialise anonymous session', e)
      }
    }

    // True only when the persisted anonymous token exists and has not expired (with clock skew buffer).
    // Using anonymousAccessToken as the primary reactive gate — clearAnonymousSession() nulls it,
    // which makes this false immediately and causes showTurnstile to re-evaluate.
    const hasValidAnonymousSession = computed(
      () =>
        !!anonymousAccessToken.value &&
        !!anonymousTokenExpiresAt.value &&
        anonymousTokenExpiresAt.value > Date.now() + CLOCK_SKEW_MS
    )

    // isAuthenticated: either Auth0-authenticated or holding a valid anonymous session
    const isAuthenticated = computed(() => _isAuthenticated.value || hasValidAnonymousSession.value)

    // isLoggedIn: authenticated via Auth0 only (not anonymous)
    const isLoggedIn = computed(() => _isAuthenticated.value)

    const getAccessToken = async (): Promise<string> =>
      _isAuthenticated.value ? await getAccessTokenSilently() : anonymousAccessToken.value ?? ''

    async function checkSessionSilently(): Promise<boolean> {
      try {
        await getAccessTokenSilently({ cacheMode: 'off' })
        return true
      } catch {
        return false
      }
    }

    // Authorization
    const authNamespace = 'https://roadfootage.com/'
    const role = computed<UserRole | null>(() => {
      if (isLoggedIn.value && user.value) {
        const roleValue = user.value[`${authNamespace}role`]
        if (roleValue === 'User' || roleValue === 'Staff') return roleValue as UserRole
        return 'User'
      }
      if (isAuthenticated.value) return 'Anonymous'
      return null
    })

    return {
      isLoading,
      isOnline,
      heartbeat,

      anonymousAccessToken,
      anonymousTokenExpiresAt,
      hasValidAnonymousSession,
      initialiseAnonymousSession,
      clearAnonymousSession,
      clearAuthSession,
      scheduleAnonymousSessionExpiry,

      loginWithRedirect,
      logout,
      user,
      role,
      isAuthenticated,
      isLoggedIn,
      isLoggingIn,
      getAccessToken,
      authError: _authError,
      checkSessionSilently
    }
  },
  {
    persist: {
      paths: ['anonymousAccessToken', 'anonymousTokenExpiresAt'],
      afterRestore(ctx) {
        // Re-schedule the expiry timeout for any token restored from storage.
        // This also handles immediate clearing of already-expired persisted tokens.
        ctx.store.scheduleAnonymousSessionExpiry()
      }
    }
  }
)
